"use client";

import { Menu, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { useSession } from "@/lib/auth/client";
import { useHydrated } from "@/lib/hooks/use-hydrated";
import type { Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/get-dictionary";
import { authHref } from "@/lib/jobs/auth-return";
import { useAuthLinkClick } from "@/lib/jobs/use-auth-link";
import { LogoutButton } from "./logout-button";
import { resolveNavGroups } from "./nav-catalog";
import { ZoneLink } from "./zone-link";

// Shared focus-visible ring treatment (see UI & Accessibility Consciousness
// in root CLAUDE.md) — extracted once so it is never retyped per link.
// Pattern: triggerBaseClass in nav-dropdown.tsx.
const focusRingClass =
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

const menuToggleButtonClass = `inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-2xl border border-border/70 bg-card text-foreground transition-colors hover:bg-accent ${focusRingClass}`;

const navSectionLinkBaseClass = `flex min-h-11 items-center rounded-xl px-2 py-2 text-sm font-medium transition-colors ${focusRingClass}`;

const accountLinkClass = `flex min-h-11 items-center rounded-xl px-2 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent ${focusRingClass}`;

const accountActionButtonClass = `flex min-h-11 w-full cursor-pointer items-center rounded-xl px-2 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-accent ${focusRingClass}`;

const primaryAccountLinkClass = `flex min-h-11 items-center rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 ${focusRingClass}`;

type MobileNavMenuProps = {
	locale: Locale;
	t: Dictionary;
	/** Flags the server navbar evaluated ON (see nav-catalog `flag`). */
	enabledFlags?: readonly string[];
};

export function MobileNavMenu({ locale, t, enabledFlags }: MobileNavMenuProps) {
	const { data: session } = useSession();
	const [isOpen, setIsOpen] = useState(false);
	// Defer the auth-dependent account links until after the client mounts.
	// better-auth's useSession hydrates from a cached cookie, so the FIRST client
	// render can resolve as authenticated while SSR (no session in this client
	// component) rendered logged-out — a hydration mismatch that discards and
	// re-renders the whole tree. NavbarAuth (the desktop counterpart) had only
	// an `isPending` gate, which assumed `isPending` is synchronously true on
	// SSR AND the first client render — a #2608 audit (T2.4) judged that
	// assumption unconfirmed and gave it this same `hydrated` mount gate. The
	// dialog is closed on load, so gating the account section costs no
	// visible flicker. See use-hydrated.ts for the shared implementation.
	const hydrated = useHydrated();
	const isAuthenticated = hydrated && Boolean(session);
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		if (isOpen) {
			dialog.showModal();
			document.body.style.overflow = "hidden";
		} else {
			dialog.close();
			document.body.style.overflow = "auto";
		}

		return () => {
			document.body.style.overflow = "auto";
		};
	}, [isOpen]);

	const pathname = usePathname();
	const closeMenu = () => setIsOpen(false);
	const authLinkClick = useAuthLinkClick(locale, closeMenu);
	const signupLabel = t.signup || (locale === "en" ? "Sign up" : "Daftar");
	const isGuest = !isAuthenticated;
	const businessOverviewHref = `/${locale}/employers`;
	const businessCollapsedLabel =
		t.navBusinessCollapsed ||
		(locale === "en" ? "For Business →" : "Untuk Bisnis →");

	// Mirrors the desktop dropdown structure (Jelajahi / Untuk Bisnis / Data)
	// as flat, non-accordion sections — the dialog already scrolls, so nested
	// disclosure widgets would only add friction. Content is the shared
	// nav-catalog.ts (no descriptions here — the drawer never renders them).
	const navSections = resolveNavGroups(t, locale, {
		includeDescriptions: false,
		forMobile: true,
		enabledFlags,
	});

	const authenticatedAccountLinks = [
		{
			href: `/${locale}/applications`,
			label: t.tracker || (locale === "en" ? "Tracker" : "Pelacak"),
		},
		{ href: `/${locale}/resume`, label: t.resumeNav || "Resume" },
		{
			href: `/${locale}/saved-searches`,
			label: t.alerts || (locale === "en" ? "Alerts" : "Notifikasi"),
		},
	];

	return (
		<>
			<button
				type="button"
				onClick={() => setIsOpen(true)}
				aria-label={t.openMenu || "Open menu"}
				aria-expanded={isOpen}
				aria-controls="mobile-nav-dialog"
				className={menuToggleButtonClass}
			>
				<Menu className="h-5 w-5" aria-hidden="true" />
			</button>

			<dialog
				id="mobile-nav-dialog"
				ref={dialogRef}
				aria-modal="true"
				aria-label={t.navigation || "Navigation"}
				onClose={closeMenu}
				onClick={(event) => {
					if (event.target === dialogRef.current) {
						closeMenu();
					}
				}}
				onKeyDown={(event) => {
					if (event.key === "Escape") {
						closeMenu();
					}
				}}
				className="m-auto h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-slate-950/55 backdrop:backdrop-blur-sm sm:hidden"
			>
				<div className="ml-auto flex h-full w-full max-w-[18rem] flex-col overscroll-y-contain border-l border-border/80 bg-background px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl">
					<div className="mb-4 flex items-center justify-between gap-3">
						<div>
							<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
								{t.navigation || "Navigation"}
							</p>
						</div>
						<button
							type="button"
							onClick={closeMenu}
							aria-label={t.closeMenu || "Close menu"}
							className={menuToggleButtonClass}
						>
							<X className="h-5 w-5" aria-hidden="true" />
						</button>
					</div>

					{/* PR-3 (2026-08-10, Task 3.3): Masuk/Daftar moved to the TOP of the
					    guest drawer — previously they sat at the bottom of a scrolling
					    list behind every nav section, the last thing a guest saw rather
					    than the first. Authenticated users keep the unchanged bottom
					    Account section (Dashboard-style links); this block renders
					    nothing once hydrated+signed-in. */}
					{isGuest && (
						<div className="mb-4 space-y-1">
							<ZoneLink
								href={authHref("login", locale, pathname)}
								onClick={authLinkClick}
								className={accountLinkClass}
							>
								{t.login}
							</ZoneLink>
							<ZoneLink
								href={authHref("signup", locale, pathname)}
								onClick={authLinkClick}
								className={primaryAccountLinkClass}
							>
								{signupLabel}
							</ZoneLink>
						</div>
					)}

					<div className="space-y-4 overflow-y-auto pb-2">
						{navSections.map((section) => {
							// PR-3 Task 3.3: collapse the "business" section behind one
							// "Untuk Bisnis →" row for guests (target ≤12 visible drawer
							// links; today's employer/business items are B2B destinations a
							// job-seeking guest rarely needs). The full list stays on
							// desktop and for authenticated users. All 6 items remain
							// reachable from /employers and the footer either way.
							const items =
								isGuest && section.key === "business"
									? [
											{
												href: businessOverviewHref,
												label: businessCollapsedLabel,
											},
										]
									: section.items;
							return (
								<div key={section.label} className="space-y-2">
									<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
										{section.label}
									</p>
									<div className="space-y-1">
										{items.map((item) => {
											const isActive =
												pathname === item.href ||
												pathname.startsWith(`${item.href}/`);
											return (
												<ZoneLink
													key={item.href}
													href={item.href}
													onClick={closeMenu}
													aria-current={isActive ? "page" : undefined}
													className={`${navSectionLinkBaseClass} ${
														isActive
															? "bg-accent text-foreground"
															: "text-foreground hover:bg-accent"
													}`}
												>
													{item.label}
												</ZoneLink>
											);
										})}
									</div>
								</div>
							);
						})}

						{isAuthenticated && (
							<div className="space-y-2">
								<p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
									{t.account || "Account"}
								</p>
								<div className="space-y-1">
									{authenticatedAccountLinks.map((item) => (
										<ZoneLink
											key={item.href}
											href={item.href}
											onClick={closeMenu}
											className={accountLinkClass}
										>
											{item.label}
										</ZoneLink>
									))}
									<LogoutButton
										label={t.logout}
										loggingOutLabel={t.loggingOut}
										className={accountActionButtonClass}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</dialog>
		</>
	);
}
